import { build_candidate_list, get_applicant_status_value } from './view_job_profile_logic_helpers';
function first_value(values = [], fallback = null) {
  return values.find((value) => value) || fallback;
}
function get_applicant_user_candidates(applicant) {
  return [
    applicant?.user,
    applicant?.applicant,
    applicant?.profile_user,
    applicant?.profile_role?.[0]?.user,
  ];
}
function get_result_score_value(result) {
  return result?.overall_score ?? result?.score ?? result?.percentage;
}
function get_applicant_direct_score_value(applicant) {
  return applicant?.score ?? applicant?.match_score;
}
function get_applicant_score_value(result, applicant) {
  return get_result_score_value(result) ?? get_applicant_direct_score_value(applicant);
}
const methods = {
    handle_edit_cancel() {
      if (this.embeddedEditModal) {
        this.$emit('cancel-edit');
        return;
      }
      this.mode = 'view';
    },
    as_count(value) {
      if (Array.isArray(value)) return value.length;
      const count = Number(value);
      return Number.isFinite(count) ? count : 0;
    },
    get_submitted_count(source) {
      const candidates = [
        source?.submitted_applicants_count,
        source?.submitted_applicant_count,
        source?.submitted_applications_count,
        source?.submitted_application_count,
        source?.submitted_count,
        source?.total_submitted_applicants,
      ];
      for (const candidate of candidates) {
        const count = Number(candidate);
        if (Number.isFinite(count)) return count;
      }
      return null;
    },
    is_submitted_applicant(applicant) {
      const submitted_flags = [
        applicant?.is_submitted,
        applicant?.submitted,
        applicant?.application_submitted,
      ];
      if (submitted_flags.some((flag) => flag === true)) return true;
      const submitted_markers = [
        applicant?.submission_status,
        applicant?.application_status,
        applicant?.status?.status,
        applicant?.status,
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());
      return submitted_markers.some((value) => value.includes('submitted'));
    },
    drawer_set_tab(tab) {
      this.drawer_active_tab = tab;
    },
    toggle_drawer_expanded() {
      this.$emit('toggle-drawer-expand', !this.drawerExpanded);
    },
    drawer_format_date(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    drawer_advert_title(advert) {
      return advert?.title || advert?.entity_type_name || this.form_detail.title || 'Job Advert';
    },
    drawer_advert_status(advert) {
      return advert?.status?.status || advert?.status || 'Active';
    },
    get_drawer_applicant_direct_count(advert) {
      return this.as_count(advert?.applicants_count ?? advert?.applicant_count ?? advert?.applications_count);
    },
    get_drawer_filtered_applicant_count(advert_id) {
      return this.drawer_applicants.filter((applicant) => {
        const applicant_advert_id = Number(applicant?.advert_id || applicant?.job_advert_id || applicant?.advert?.id);
        return applicant_advert_id === advert_id && this.is_submitted_applicant(applicant);
      }).length;
    },
    drawer_advert_applicant_count(advert) {
      const submitted_count = this.get_submitted_count(advert);
      if (submitted_count !== null) return submitted_count;
      const direct = this.get_drawer_applicant_direct_count(advert);
      if (direct) return direct;
      const advert_id = Number(advert?.id);
      if (!advert_id) return 0;
      return this.get_drawer_filtered_applicant_count(advert_id);
    },
    get_drawer_applicant_user(applicant) {
      return first_value(get_applicant_user_candidates(applicant), {});
    },
    join_non_empty_name_parts(parts = []) {
      return parts.filter(Boolean).join(' ').trim();
    },
    get_applicant_primary_name_candidates(applicant) {
      return build_candidate_list([
        applicant?.entity_name,
        applicant?.full_name,
        applicant?.applicant_name,
        applicant?.name,
        applicant?.display_name,
      ]);
    },
    get_applicant_secondary_name_candidates(applicant, user) {
      return build_candidate_list([
        applicant?.surname_and_initials || Reflect.get(applicant || {}, 'Surname and Initials'),
        this.join_non_empty_name_parts([
          applicant?.first_name,
          applicant?.middle_name,
          applicant?.last_name,
        ]),
        this.join_non_empty_name_parts([
          user?.first_name,
          user?.middle_name,
          user?.last_name,
        ]),
      ]);
    },
    get_drawer_applicant_name_candidates(applicant, user) {
      return [
        ...this.get_applicant_primary_name_candidates(applicant),
        ...this.get_applicant_secondary_name_candidates(applicant, user),
      ];
    },
    drawer_applicant_name(applicant) {
      const user = this.get_drawer_applicant_user(applicant);
      return this.get_drawer_applicant_name_candidates(applicant, user)
        .find((value) => value) || 'Applicant';
    },
    get_applicant_direct_job_title_candidates(applicant) {
      return build_candidate_list([
        applicant?.advert_title,
        applicant?.job_title,
        applicant?.job_profile_title,
        applicant?.job_applied_for,
        Reflect.get(applicant || {}, 'Job Applied For'),
      ]);
    },
    get_applicant_nested_job_title_candidates(applicant) {
      return build_candidate_list([
        applicant?.advert?.title,
        applicant?.advert?.advert_title,
        applicant?.advert?.reference_number,
        applicant?.job_advert?.title,
        applicant?.job_advert?.advert_title,
        this.form_detail.title,
      ]);
    },
    get_drawer_applicant_job_title_candidates(applicant) {
      return [
        ...this.get_applicant_direct_job_title_candidates(applicant),
        ...this.get_applicant_nested_job_title_candidates(applicant),
      ];
    },
    drawer_applicant_job_title(applicant) {
      return this.get_drawer_applicant_job_title_candidates(applicant).find((value) => value) || 'Job Advert';
    },
    drawer_applicant_initials(applicant) {
      return this.drawer_applicant_name(applicant)
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('') || 'A';
    },
    drawer_applicant_reference(applicant) {
      const fallback_id = String(applicant?.id || '').padStart(4, '0');
      return first_value([
        applicant?.application_number,
        applicant?.reference_number,
        applicant?.application_ref,
      ], `APP-${fallback_id}`);
    },
    get_drawer_applicant_score_result(applicant) {
      const score_sources = [
        applicant?.orcaa_response?.score_result,
        applicant?.orcaa_response?.generated_work?.score_result,
        applicant?.score_result,
        applicant?.ai_assessment?.score_result,
        applicant?.recruitment_assessment?.score_result,
      ];
      return score_sources.find((value) => value) || {};
    },
    drawer_applicant_score(applicant) {
      const result = this.get_drawer_applicant_score_result(applicant);
      const number = Number(get_applicant_score_value(result, applicant));
      if (!Number.isFinite(number)) return '';
      return `${Math.round(number)}%`;
    },
    get_drawer_applicant_status(applicant) {
      return get_applicant_status_value(applicant).toLowerCase();
    },
    get_bucket_from_score(score) {
      if (score >= 80) return 'qualified';
      if (score >= 50) return 'partial';
      return 'unqualified';
    },
    drawer_applicant_bucket(applicant) {
      const status = this.get_drawer_applicant_status(applicant);
      const status_bucket = [
        ['unqualified', 'unqualified'],
        ['partial', 'partial'],
        ['qualified', 'qualified'],
        ['shortlisted', 'qualified'],
      ].find(([keyword]) => status.includes(keyword));
      if (status_bucket) return status_bucket[1];
      const score = parseInt(this.drawer_applicant_score(applicant), 10);
      if (Number.isFinite(score)) return this.get_bucket_from_score(score);
      return 'unqualified';
    },
    drawer_applicant_status_label(applicant) {
      const status = get_applicant_status_value(applicant);
      if (status.toLowerCase().includes('shortlisted')) return 'Shortlisted';
      if (status) return status.replace(/_/g, ' ');
      return this.drawer_applicant_bucket(applicant) === 'qualified' ? 'Qualified' : 'Pending';
    },
};
export default methods;
